import { type FC } from "react";
import { Box, Group, Stack, UnstyledButton } from "@mantine/core";
import Text from "@/components/atoms/Text";
import { useTranslation } from "react-i18next";
import { ReceptionFractionWindow, ReceptionWindowGranularity } from "@/types";
import styles from "./FractionWindowCell.module.css";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import Badge from "@/components/atoms/Badge";

dayjs.extend(isoWeek);

interface Props {
  periodStart: string;
  granularity: ReceptionWindowGranularity;
  fractionNames: string[];
  window: ReceptionFractionWindow | undefined;
  isActive?: boolean;
  onClick: () => void;
}

const FractionWindowCell: FC<Props> = ({ periodStart, granularity, fractionNames, window: win, isActive, onClick }) => {
  const { t } = useTranslation();

  const isClosed = win?.closed ?? false;
  const isAll = !isClosed && (win?.acceptAll ?? true);

  const stateClass = `${isClosed ? styles.closed : isAll ? styles.open : ""} ${isActive ? styles.active : ""}`;

  const pills = isClosed ? null : isAll ? (
    <Badge radius="xs" color="selection">
      {t("settings:reception.fractionWindows.all")}
    </Badge>
  ) : (
    fractionNames.map((name) => (
      <Badge key={name} radius="xs" color="selection">
        {name}
      </Badge>
    ))
  );

  const baseStatusText = isClosed
    ? t("settings:reception.fractionWindows.closed")
    : isAll
      ? t("settings:reception.fractionWindows.open")
      : t("settings:reception.fractionWindows.fractions", { count: fractionNames.length });

  const statusText = isActive
    ? `${baseStatusText} · ${t("settings:reception.fractionWindows.editing")}`
    : baseStatusText;

  if (granularity === "MONTH") {
    const monthLabel = dayjs(periodStart).format("MMM").toUpperCase();

    return (
      <UnstyledButton
        onClick={onClick}
        className={`${styles.card} ${stateClass}`}
        data-testid={`fraction-window-cell-${periodStart}`}
      >
        <Stack>
          <Box>
            <Text size="xs" display="technical" tt="uppercase" fw={400}>
              {monthLabel}
            </Text>
            {pills && (
              <Group gap={4} mt={4} wrap="wrap">
                {pills}
              </Group>
            )}
          </Box>

          <Text size="xs" color="secondary" mt={4}>
            {statusText}
          </Text>
        </Stack>
      </UnstyledButton>
    );
  }

  const weekNumber = dayjs(periodStart).isoWeek();
  const dateLabel = dayjs(periodStart).format("D MMM").toUpperCase();
  const weekPrefix = t("settings:reception.fractionWindows.granularity.week").toUpperCase();
  const rowLabel = `${weekPrefix} ${weekNumber} · ${dateLabel}`;

  return (
    <UnstyledButton
      onClick={onClick}
      className={`${styles.row} ${stateClass}`}
      data-testid={`fraction-window-cell-${periodStart}`}
    >
      <Text size="xs" display="technical" fw={400} tt="uppercase" className={styles.rowLabel}>
        {granularity === "YEAR" ? String(dayjs(periodStart).year()) : rowLabel}
      </Text>
      {pills && (
        <Group gap={4} wrap="wrap">
          {pills}
        </Group>
      )}
      <Text size="xs" color="secondary" tt="uppercase" className={styles.rowStatus}>
        {statusText}
      </Text>
    </UnstyledButton>
  );
};

export default FractionWindowCell;
